/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import type {ID} from './github/types';

import {gitHubClientAtom} from './jotai';
import useRefreshPullRequest from './useRefreshPullRequest';
import {Text} from '@primer/react';
import {useAtomValue} from 'jotai';
import {useCallback, useState} from 'react';

type Props = {
  threadId: ID;
  isResolved: boolean;
};

export default function ResolveThreadButton({
  threadId,
  isResolved,
}: Props): React.ReactElement {
  const client = useAtomValue(gitHubClientAtom);
  const refreshPullRequest = useRefreshPullRequest();
  const [loading, setLoading] = useState(false);

  const onClick = useCallback(async () => {
    if (client == null) {
      return;
    }

    setLoading(true);
    try {
      if (isResolved) {
        await client.unresolveReviewThread(threadId);
      } else {
        await client.resolveReviewThread(threadId);
      }
      refreshPullRequest();
    } finally {
      setLoading(false);
    }
  }, [client, isResolved, threadId, refreshPullRequest]);

  const label = loading
    ? isResolved
      ? 'Unresolving...'
      : 'Resolving...'
    : isResolved
      ? 'Unresolve'
      : 'Resolve conversation';

  return (
    <Text
      as="button"
      onClick={onClick}
      disabled={loading}
      fontSize={12}
      sx={{
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: loading ? 'default' : 'pointer',
        opacity: loading ? 0.6 : 1,
        color: 'accent.fg',
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline',
        },
      }}>
      {label}
    </Text>
  );
}
